/**
 * Admin: tabela de R$/h por usuario.
 *
 * So inovacao_admin acessa (backend ja bloqueia). Mostra TODOS os usuarios
 * do CRM com input editavel + indica se taxa ja foi cadastrada antes ou
 * eh primeira definicao.
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { DollarSign, Save, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminCustosAPI } from '../../services/inovacaoAPI';
import { authAPI } from '../../services/api';
import { mensagemErro } from './labels';

export default function AdminCustosHora() {
  const [usuarios, setUsuarios] = useState([]);
  const [taxas, setTaxas] = useState({}); // user_id -> { valor, atualizado_em }
  const [edits, setEdits] = useState({}); // user_id -> string em edicao
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');

  function carregar() {
    setLoading(true);
    Promise.all([authAPI.listUsers(), adminCustosAPI.listarTaxas()])
      .then(([users, lista]) => {
        setUsuarios(users.data || []);
        const map = {};
        for (const t of lista) {
          map[t.usuario.id] = { valor: t.valor_hora_brl, atualizado_em: t.atualizado_em };
        }
        setTaxas(map);
      })
      .catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar')))
      .finally(() => setLoading(false));
  }

  useEffect(() => { carregar(); }, []);

  async function salvar(uid) {
    const valor = parseFloat(edits[uid]);
    if (isNaN(valor) || valor < 0) {
      toast.error('Valor invalido'); return;
    }
    setSavingId(uid);
    try {
      const r = await adminCustosAPI.definirTaxa(uid, valor);
      setTaxas((x) => ({ ...x, [uid]: { valor: r.valor_hora_brl, atualizado_em: r.atualizado_em } }));
      setEdits((x) => { const c = { ...x }; delete c[uid]; return c; });
      toast.success('Taxa salva');
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao salvar'));
    }
    setSavingId(null);
  }

  const usuariosFiltrados = filtro
    ? usuarios.filter((u) => (u.nome_completo || '').toLowerCase().includes(filtro.toLowerCase())
                          || (u.email || '').toLowerCase().includes(filtro.toLowerCase()))
    : usuarios;

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      <Link to="/inovacao" style={{ color: '#7f8c8d', textDecoration: 'none', fontSize: '0.85rem',
                                     display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={14} /> Gestão de Projetos
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 16 }}>
        <DollarSign size={28} color="var(--brand, #20a490)" />
        <div>
          <h1 style={{ margin: 0, color: '#2c3e50' }}>Custo por hora dos usuários</h1>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '0.88rem' }}>
            Define o valor que entra no cálculo de custo R$ de cada fase. Mudanças retroagem em projetos ativos.
          </p>
        </div>
      </div>

      <input value={filtro} onChange={(e) => setFiltro(e.target.value)}
             placeholder="Buscar por nome ou email…"
             style={{ width: '100%', maxWidth: 400, padding: 10, marginBottom: 16,
                      border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem' }} />

      {loading ? <p style={{ color: '#bdc3c7' }}>Carregando…</p> : (
        <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th style={th}>Usuário</th>
                <th style={th}>R$/h atual</th>
                <th style={th}>Atualizado em</th>
                <th style={th}>Editar</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map((u) => {
                const taxa = taxas[u.id];
                const valorAtual = taxa ? parseFloat(taxa.valor) : null;
                const editing = u.id in edits;
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid #ecf0f1' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 500, color: '#2c3e50' }}>{u.nome_completo}</div>
                      <div style={{ fontSize: '0.78rem', color: '#7f8c8d' }}>{u.email}</div>
                    </td>
                    <td style={td}>
                      {valorAtual !== null ? formatarBRL(valorAtual)
                        : <span style={{ color: '#e67e22', fontSize: '0.85rem' }}>sem taxa</span>}
                    </td>
                    <td style={{ ...td, color: '#7f8c8d', fontSize: '0.78rem' }}>
                      {taxa?.atualizado_em ? new Date(taxa.atualizado_em).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>R$</span>
                        <input type="number" min="0" step="0.01"
                               value={editing ? edits[u.id] : (valorAtual ?? '')}
                               onChange={(e) => setEdits((x) => ({ ...x, [u.id]: e.target.value }))}
                               style={{ width: 100, padding: 6, border: '1px solid #ddd',
                                        borderRadius: 4, fontSize: '0.88rem' }} />
                        <button onClick={() => salvar(u.id)} disabled={savingId === u.id || !editing}
                                style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none',
                                         borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
                                         opacity: (savingId === u.id || !editing) ? 0.5 : 1,
                                         display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '0.8rem' }}>
                          <Save size={12} /> Salvar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {usuariosFiltrados.length === 0 && (
            <p style={{ textAlign: 'center', padding: 20, color: '#bdc3c7' }}>
              Nenhum usuário encontrado.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function formatarBRL(n) {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const th = { textAlign: 'left', padding: '12px 14px', fontSize: '0.72rem',
             color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 };
const td = { padding: '12px 14px', verticalAlign: 'middle' };
